import React, { useEffect, useMemo, useState } from 'react';
import { FiRefreshCw, FiSearch } from 'react-icons/fi';
import Navbar from '../../components/navbar/Navbar';
import './StatsRma.css';
import { isSupervisor as isSupervisorFn } from '../../utils/session';

const PERIODS = [
  { label: '1 jour', value: 1 },
  { label: '7 jours', value: 7 },
  { label: '30 jours', value: 30 },
];

const ALL_USERS = '__all__';
const ALL_ORGANISATIONS = '__all__';
const UNKNOWN_USER = 'Non renseigné';

const pad = (value) => String(value).padStart(2, '0');

const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  return `${year}-${month}-${day}`;
};

const getValidationDateKey = (row) => {
  if (row.date) return row.date;
  if (!row.date_validation_organisation) return '';
  return String(row.date_validation_organisation).split('T')[0].split(' ')[0];
};

const formatDate = (value) => {
  if (!value) return '-';
  const [year, month, day] = String(value).split('T')[0].split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

const formatShortDate = (value) => {
  const formatted = formatDate(value);
  return formatted === '-' ? '-' : formatted.slice(0, 5);
};

const shouldShowChartDate = (row, index, totalDays) => {
  if (totalDays <= 14) return true;
  if (index === 0 || index === totalDays - 1) return true;

  const [, , day] = row.date.split('-');
  if (day === '01') return true;

  const interval = totalDays <= 30 ? 3 : 7;
  return index % interval === 0;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const normalized = String(value).replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return formatDate(value);

  return `${formatDate(toDateKey(date))} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const addDays = (dateKey, offset) => {
  const date = new Date(`${dateKey}T12:00:00`);
  date.setDate(date.getDate() + offset);
  return toDateKey(date);
};

const getInclusiveDays = (startDate, endDate) => {
  const start = new Date(`${startDate}T12:00:00`);
  const end = new Date(`${endDate}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return 1;
  return Math.floor((end - start) / 86400000) + 1;
};

const getDateRangeForPeriod = (periodDays) => {
  const end = new Date();
  end.setHours(12, 0, 0, 0);
  const start = new Date(end);
  start.setDate(start.getDate() - periodDays + 1);
  return {
    startDate: toDateKey(start),
    endDate: toDateKey(end),
  };
};

const buildCompleteDailyStats = (rows, startDate, endDate) => {
  const countsByDate = new Map(
    rows.map((row) => [row.date, Number(row.count) || 0])
  );
  const totalDays = getInclusiveDays(startDate, endDate);

  return Array.from({ length: totalDays }, (_, index) => {
    const key = addDays(endDate, -index);

    return {
      date: key,
      count: countsByDate.get(key) || 0,
    };
  });
};

const pluralize = (value, singular, plural) => `${value} ${value > 1 ? plural : singular}`;

const StatsRma = () => {
  const [stats, setStats] = useState([]);
  const [validations, setValidations] = useState([]);
  const [users, setUsers] = useState([]);
  const [userSummary, setUserSummary] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalUnfiltered, setTotalUnfiltered] = useState(0);
  const initialRange = useMemo(() => getDateRangeForPeriod(30), []);
  const [days, setDays] = useState(30);
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [activePeriod, setActivePeriod] = useState(30);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [userFilter, setUserFilter] = useState(ALL_USERS);
  // null = pas encore choisie : au premier chargement on retient la première
  // organisation renvoyée par l'API (un user client n'en reçoit qu'une).
  const [organisationFilter, setOrganisationFilter] = useState(null);
  const [organisations, setOrganisations] = useState([]);
  const [canChooseOrganisation, setCanChooseOrganisation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const canViewStats = useMemo(() => {
    return localStorage.getItem('stats_organisation') === 'true' || isSupervisorFn();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError('');

      try {
        const token = localStorage.getItem('access_token');
        const params = new URLSearchParams({
          days: String(Math.min(days, 365)),
          start_date: startDate,
          end_date: endDate,
        });

        if (debouncedSearch) params.set('search', debouncedSearch);
        if (userFilter !== ALL_USERS) params.set('user', userFilter);
        if (organisationFilter && organisationFilter !== ALL_ORGANISATIONS) {
          params.set('organisation_id', organisationFilter);
        }

        const res = await fetch(`/api/stats/rma-daily?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.status === 403) {
          setError("Vous n'avez pas accès aux statistiques de cette organisation.");
          setStats([]);
          setValidations([]);
          setUsers([]);
          setUserSummary([]);
          setTotal(0);
          setTotalUnfiltered(0);
          return;
        }

        if (!res.ok) throw new Error('Erreur lors du chargement des statistiques.');

        const data = await res.json();
        const orgs = Array.isArray(data.organisations) ? data.organisations : [];
        setOrganisations(orgs);
        setCanChooseOrganisation(Boolean(data.can_choose_organisation));
        if (organisationFilter === null && orgs.length > 0) {
          setOrganisationFilter(String(orgs[0].id));
          return; // le changement de filtre relance le chargement sur cette organisation
        }
        setStats(Array.isArray(data.stats) ? data.stats : []);
        setValidations(Array.isArray(data.validations) ? data.validations : []);
        setUsers(Array.isArray(data.users) ? data.users : []);
        setUserSummary(Array.isArray(data.user_summary) ? data.user_summary : []);
        setTotal(data.total || 0);
        setTotalUnfiltered(data.total_unfiltered || data.total || 0);
      } catch (err) {
        setError(err.message || 'Erreur lors du chargement des statistiques.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [days, startDate, endDate, debouncedSearch, userFilter, organisationFilter]);

  const selectedOrganisationName = useMemo(() => {
    if (organisationFilter === ALL_ORGANISATIONS) return 'toutes organisations';
    const org = organisations.find((o) => String(o.id) === organisationFilter);
    return org ? org.name : '';
  }, [organisations, organisationFilter]);

  const handleOrganisationChange = (value) => {
    // Les utilisateurs listés dépendent de l'organisation : on repart de « tous ».
    setUserFilter(ALL_USERS);
    setOrganisationFilter(value);
  };

  const userOptions = useMemo(() => {
    return users.map((row) => row.username || UNKNOWN_USER);
  }, [users]);

  const normalizedSearch = debouncedSearch.toLowerCase();
  const hasActiveFilters = normalizedSearch.length > 0 || userFilter !== ALL_USERS || activePeriod === null;
  const dailyStats = useMemo(() => buildCompleteDailyStats(stats, startDate, endDate), [stats, startDate, endDate]);
  const chartStats = useMemo(() => [...dailyStats].reverse(), [dailyStats]);
  const periodLabel = activePeriod ? `les ${days} derniers jours` : `du ${formatDate(startDate)} au ${formatDate(endDate)}`;
  const maxCount = useMemo(
    () => Math.max(...dailyStats.map((row) => row.count), 0),
    [dailyStats]
  );

  const todayKey = toDateKey(new Date());
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterdayKey = toDateKey(yesterdayDate);

  const todayCount = dailyStats.find((row) => row.date === todayKey)?.count || 0;
  const yesterdayCount = dailyStats.find((row) => row.date === yesterdayKey)?.count || 0;
  const average = days > 0 ? total / days : 0;
  const activeDays = dailyStats.filter((row) => row.count > 0).length;
  const avgActiveDay = activeDays > 0 ? total / activeDays : 0;
  const validatorsCount = userSummary.length;
  const trendVsYesterday = todayCount - yesterdayCount;

  const bestDay = dailyStats.reduce(
    (best, row) => (row.count > best.count ? row : best),
    { date: null, count: 0 }
  );

  const latestValidation = validations[0];
  const recentValidations = validations.slice(0, 6);
  const busiestDays = [...dailyStats]
    .filter((row) => row.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  const topUser = userSummary[0];

  const applyPeriod = (periodDays) => {
    const range = getDateRangeForPeriod(periodDays);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setDays(periodDays);
    setActivePeriod(periodDays);
  };

  const handleStartDateChange = (value) => {
    const nextStart = value || startDate;
    const nextEnd = nextStart > endDate ? nextStart : endDate;
    setStartDate(nextStart);
    setEndDate(nextEnd);
    setDays(getInclusiveDays(nextStart, nextEnd));
    setActivePeriod(null);
  };

  const handleEndDateChange = (value) => {
    const nextEnd = value || endDate;
    const nextStart = nextEnd < startDate ? nextEnd : startDate;
    setStartDate(nextStart);
    setEndDate(nextEnd);
    setDays(getInclusiveDays(nextStart, nextEnd));
    setActivePeriod(null);
  };

  const resetFilters = () => {
    applyPeriod(30);
    setSearchTerm('');
    setDebouncedSearch('');
    setUserFilter(ALL_USERS);
  };

  const primaryKpis = [
    {
      label: "Aujourd'hui",
      value: todayCount,
      helper: `${trendVsYesterday >= 0 ? '+' : ''}${trendVsYesterday} vs hier`,
      tone: trendVsYesterday > 0 ? 'good' : trendVsYesterday < 0 ? 'warn' : 'neutral',
      progress: maxCount > 0 ? Math.round((todayCount / maxCount) * 100) : 0,
    },
    {
      label: hasActiveFilters ? 'Résultat filtré' : 'Total période',
      value: total,
      helper: hasActiveFilters ? `${total}/${totalUnfiltered} dossiers` : pluralize(days, 'jour analysé', 'jours analysés'),
      tone: 'blue',
      progress: totalUnfiltered > 0 ? Math.round((total / totalUnfiltered) * 100) : 0,
    },
    {
      label: 'Moyenne active',
      value: avgActiveDay.toFixed(1),
      helper: `${average.toFixed(1)} / jour calendrier`,
      tone: 'indigo',
      progress: average > 0 ? Math.min(100, Math.round((avgActiveDay / Math.max(average, 1)) * 55)) : 0,
    },
  ];

  const secondaryKpis = [
    {
      label: 'Meilleur jour',
      value: bestDay.count,
      helper: bestDay.date ? formatDate(bestDay.date) : '-',
    },
    {
      label: 'Utilisateurs actifs',
      value: validatorsCount,
      helper: topUser ? `${topUser.username} en tête` : 'Aucun validateur',
    },
    {
      label: 'Dernière validation',
      value: latestValidation ? formatDateTime(latestValidation.date_validation_organisation).split(' ')[1] : '-',
      helper: latestValidation ? `${latestValidation.immatriculation || latestValidation.numero_du_dossier || '-'} le ${formatDate(getValidationDateKey(latestValidation))}` : 'Aucune validation',
    },
  ];

  return (
    <div className="stats-rma-page">
      <Navbar />
      <main className="stats-rma-content">
        <div className="stats-rma-hero">
          <div className="stats-rma-title-block">
            <h1>
              {canChooseOrganisation
                ? 'Statistiques des organisations'
                : `Statistiques ${selectedOrganisationName}`}
            </h1>
            <p>Vue opérationnelle des validations de l'organisation, des rythmes journaliers et de l'activité par utilisateur.</p>
          </div>
          {canViewStats && (
            <div className="stats-rma-period-card" aria-label="Période analysée">
              <span>Période analysée{canChooseOrganisation && selectedOrganisationName ? ` · ${selectedOrganisationName}` : ''}</span>
              <strong>{formatDate(startDate)} - {formatDate(endDate)}</strong>
              <small>{pluralize(days, 'jour', 'jours')} · {totalUnfiltered} validations</small>
            </div>
          )}
        </div>

        {!canViewStats && (
          <div className="stats-rma-message">Accès non autorisé.</div>
        )}

        {canViewStats && (
          <>
            {loading ? (
              <div className="stats-rma-message">Chargement...</div>
            ) : error ? (
              <div className="stats-rma-message error">{error}</div>
            ) : (
              <>
                <section className="stats-rma-filters" aria-label="Filtres statistiques">
                  <label className="stats-rma-date-filter">
                    <span>Date début</span>
                    <input
                      type="date"
                      value={startDate}
                      max={endDate}
                      onChange={(event) => handleStartDateChange(event.target.value)}
                      aria-label="Date début"
                    />
                  </label>
                  <label className="stats-rma-date-filter">
                    <span>Date fin</span>
                    <input
                      type="date"
                      value={endDate}
                      min={startDate}
                      onChange={(event) => handleEndDateChange(event.target.value)}
                      aria-label="Date fin"
                    />
                  </label>
                  <div className="stats-rma-shortcuts" aria-label="Raccourcis de période">
                    <span>Raccourci</span>
                    <div className="stats-rma-periods">
                      {PERIODS.map((period) => (
                        <button
                          key={period.value}
                          type="button"
                          className={`stats-rma-period${activePeriod === period.value ? ' active' : ''}`}
                          onClick={() => applyPeriod(period.value)}
                        >
                          {period.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {canChooseOrganisation && (
                    <label className="stats-rma-user-filter">
                      <span>Organisation</span>
                      <select
                        value={organisationFilter ?? ''}
                        onChange={(event) => handleOrganisationChange(event.target.value)}
                        aria-label="Choisir l'organisation"
                      >
                        {organisations.map((org) => (
                          <option value={String(org.id)} key={org.id}>{org.name}</option>
                        ))}
                        <option value={ALL_ORGANISATIONS}>Toutes les organisations</option>
                      </select>
                    </label>
                  )}
                  <label className="stats-rma-user-filter">
                    <span>Utilisateur</span>
                    <select
                      value={userFilter}
                      onChange={(event) => setUserFilter(event.target.value)}
                      aria-label="Filtrer par utilisateur"
                    >
                      <option value={ALL_USERS}>Tous les utilisateurs</option>
                      {userOptions.map((username) => (
                        <option value={username} key={username}>{username}</option>
                      ))}
                    </select>
                  </label>
                  <div className="stats-rma-search">
                    <span aria-hidden="true"><FiSearch /></span>
                    <input
                      type="search"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Rechercher dossier, utilisateur ou date"
                      aria-label="Rechercher dans les validations"
                    />
                  </div>
                  <div className="stats-rma-filter-actions">
                    <div className="stats-rma-filter-summary">
                      <strong>{total}</strong>
                      <span>{hasActiveFilters ? `sur ${totalUnfiltered} validations` : 'validations affichées'}</span>
                    </div>
                    {hasActiveFilters && (
                      <button type="button" className="stats-rma-reset" onClick={resetFilters}>
                        <FiRefreshCw aria-hidden="true" />
                        Réinitialiser
                      </button>
                    )}
                  </div>
                </section>

                <section className="stats-rma-overview" aria-label="Indicateurs">
                  {primaryKpis.map((kpi) => (
                    <article className={`stats-rma-kpi ${kpi.tone}`} key={kpi.label}>
                      <div className="stats-rma-kpi-label">
                        <span>{kpi.label}</span>
                        <i aria-hidden="true" />
                      </div>
                      <strong>{kpi.value}</strong>
                      <small>{kpi.helper}</small>
                      <div className="stats-rma-kpi-meter" aria-hidden="true">
                        <i style={{ width: `${Math.max(4, Math.min(kpi.progress, 100))}%` }} />
                      </div>
                    </article>
                  ))}
                </section>

                <section className="stats-rma-insights" aria-label="Synthèse rapide">
                  {secondaryKpis.map((kpi) => (
                    <article className="stats-rma-insight" key={kpi.label}>
                      <span>{kpi.label}</span>
                      <strong>{kpi.value}</strong>
                      <small>{kpi.helper}</small>
                    </article>
                  ))}
                </section>

                <section className="stats-rma-layout">
                  <div className="stats-rma-panel stats-rma-chart-panel">
                    <div className="stats-rma-panel-head">
                      <div>
                        <h2>Volume journalier</h2>
                        <p>{pluralize(total, 'validation', 'validations')} sur {periodLabel}</p>
                      </div>
                      <span className="stats-rma-panel-meta">Max {maxCount}</span>
                    </div>

                    <div className="stats-rma-chart" role="list" aria-label="Validations par jour">
                      {chartStats.map((row, index) => {
                        const height = maxCount > 0 ? Math.max(10, Math.round((row.count / maxCount) * 132)) : 10;
                        const showLabel = shouldShowChartDate(row, index, chartStats.length);
                        return (
                          <div
                            className="stats-rma-bar-item"
                            key={row.date}
                            role="listitem"
                            aria-label={`${formatDate(row.date)} : ${row.count} validation${row.count > 1 ? 's' : ''}`}
                          >
                            <div className="stats-rma-bar-track">
                              {row.count > 0 && <b>{row.count}</b>}
                              <div
                                className={`stats-rma-bar${row.count === maxCount && maxCount > 0 ? ' best' : ''}${row.count === 0 ? ' empty' : ''}`}
                                style={{ height: `${height}px` }}
                                title={`${formatDate(row.date)} : ${row.count}`}
                              />
                            </div>
                            <span className={!showLabel ? 'muted' : ''}>{showLabel ? formatShortDate(row.date) : ''}</span>
                          </div>
                        );
                      })}
                    </div>

                    <div className="stats-rma-chart-footer" aria-label="Résumé du volume journalier">
                      <div>
                        <span>Jours actifs</span>
                        <strong>{activeDays}/{days}</strong>
                      </div>
                      <div>
                        <span>Moyenne</span>
                        <strong>{average.toFixed(1)}</strong>
                      </div>
                      <div>
                        <span>Pic</span>
                        <strong>{bestDay.date ? formatShortDate(bestDay.date) : '-'}</strong>
                      </div>
                    </div>
                  </div>

                  <aside className="stats-rma-panel stats-rma-side-panel">
                    <div className="stats-rma-panel-head compact">
                      <h2>Classement utilisateurs</h2>
                      <span className="stats-rma-panel-meta">Top {userSummary.length}</span>
                    </div>
                    {userSummary.length > 0 ? (
                      <div className="stats-rma-user-list">
                        {userSummary.slice(0, 5).map((user, index) => (
                          <div className="stats-rma-user-row" key={user.username}>
                            <span className="stats-rma-rank">#{index + 1}</span>
                            <div>
                              <strong>{user.username}</strong>
                              <small>Dernière : {formatDateTime(user.lastValidation)}</small>
                            </div>
                            <b>{user.count}</b>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="stats-rma-empty compact">Aucune validation</div>
                    )}
                  </aside>
                </section>

                <section className="stats-rma-lists">
                  <div className="stats-rma-panel stats-rma-recent-panel">
                    <div className="stats-rma-panel-head compact">
                      <h2>Dernières validations</h2>
                      <span className="stats-rma-panel-meta">{total} missions</span>
                    </div>
                    {recentValidations.length > 0 ? (
                      <div className="stats-rma-recent-list">
                        {recentValidations.map((row) => (
                          <div className="stats-rma-recent-row" key={`${row.id}-${row.date_validation_organisation}`}>
                            <div>
                              <strong>{row.immatriculation || row.numero_du_dossier || '-'}</strong>
                              <small>{row.validated_by_username || UNKNOWN_USER}</small>
                            </div>
                            <time>{formatDateTime(row.date_validation_organisation)}</time>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="stats-rma-empty compact">Aucune validation sur cette période</div>
                    )}
                  </div>

                  <div className="stats-rma-panel stats-rma-busy-panel">
                    <div className="stats-rma-panel-head compact">
                      <h2>Jours les plus actifs</h2>
                      <span className="stats-rma-panel-meta">Top 3</span>
                    </div>
                    {busiestDays.length > 0 ? (
                      <div className="stats-rma-busy-list">
                        {busiestDays.map((row) => (
                          <div className="stats-rma-busy-row" key={row.date}>
                            <span>{formatDate(row.date)}</span>
                            <div className="stats-rma-busy-meter">
                              <i style={{ width: `${maxCount > 0 ? (row.count / maxCount) * 100 : 0}%` }} />
                            </div>
                            <strong>{row.count}</strong>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="stats-rma-empty compact">Aucun jour actif</div>
                    )}
                  </div>
                </section>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default StatsRma;
